import React, { forwardRef, useContext, useImperativeHandle, useEffect } from 'react'
import { CustomSelect } from '@strata-monorepo/shared-ui/components/atoms/CustomSelect/index'
import { Toggle } from '@strata-monorepo/shared-ui/components/Toggle/Toggle'
import { type FormStepProps, OdysseyContext } from '@redventures/odyssey'
import useFieldSelected from '@strata-monorepo/tracking/src/hooks/useFieldSelected'
import { useStoreActions, useStoreState } from '../../store/useStore'
import type { CohesionGlobal } from '@strata-monorepo/shared-types/src/window'
import type { FormStepRef } from './formStepRef'
import { useIncense } from '../../hooks/useIncense'
import { complementEnrichmentWithFormData } from '../../utils/complementEnrichmentWithFormData'
import { prefillHomeInfoFromPropertyData } from '../../utils/prefillHomeInfoFromPropertyData'
import ArrowDownExpand from '@strata-monorepo/shared-ui/images/icons/ArrowDownExpand'
import { QuestionFlowEnum } from '@strata-monorepo/shared-types'
import { oneThroughFivePlusOptions } from '../../constants/options/oneThroughFivePlus'
import { oneThroughFourPlusOptions } from '../../constants/options/oneThroughFourPlus'

const yearBuiltOptions = [
  { key: '1940-1959', value: '1940-1959', label: '1940-1959' },
  { key: '1960-1969', value: '1960-1969', label: '1960-1969' },
  { key: '1970-1979', value: '1970-1979', label: '1970-1979' },
  { key: '1980-1989', value: '1980-1989', label: '1980-1989' },
  { key: '1990-1999', value: '1990-1999', label: '1990-1999' },
  { key: '2000-2009', value: '2000-2009', label: '2000-2009' },
  { key: '2010-2019', value: '2010-2019', label: '2010-2019' },
  { key: '2020 or newer', value: '2020 or newer', label: '2020 or newer' },
  { key: 'Unsure', value: 'unknown', label: 'Unsure' },
]

interface HomeInfoComponentProps {
  formValue: string
  shouldFetchConsumptionPrediction?: boolean
  tracking?: {
    stepName: string
    stepId: string
    stepNumber: number
  }
}

const HomeInfo = forwardRef<FormStepRef, FormStepProps>(({ formStep }, ref) => {
  const odysseyContext = useContext(OdysseyContext)

  const { componentProps } = formStep
  const { tracking, shouldFetchConsumptionPrediction } = componentProps as HomeInfoComponentProps

  const trackFieldSelected = useFieldSelected()
  const formId = useStoreState((state) => state.form.formId)
  const formVersion = useStoreState((state) => state.form.formVersion)
  const currentStepIndex = useStoreState((state) => state.form.currentStepIndex)
  const formType = useStoreState((state) => state.form.formType)

  const fetchConsumptionPrediction = useStoreActions(
    (actions) => actions.consumptionPrediction.fetchConsumptionPrediction,
  )

  const enrichCustomerResponse = useStoreState(
    (state) => state.enrichCustomer.enrichCustomerResponse,
  )

  const setEnrichCustomerResponse = useStoreActions(
    (actions) => actions.enrichCustomer.setEnrichCustomerResponse,
  )

  const incense = useIncense()

  // Form key to tracking field name (for FieldSelected eventing)
  const formKeyToTrackingField: Record<string, string> = {
    'homeInfo.homeSize': 'home_details_size',
    'homeInfo.numBedrooms': 'home_details_bedrooms',
    'homeInfo.numBathrooms': 'home_details_bathrooms',
    'homeInfo.numFloors': 'home_details_floors',
    'homeInfo.yearBuilt': 'home_details_year_built',
  }

  // Prefill form fields from propertyData when available and fields are empty
  useEffect(() => {
    const propertyData = enrichCustomerResponse?.results?.propertyData

    if (!propertyData) {
      return
    }

    // Get prefilled values from propertyData
    const prefilledValues = prefillHomeInfoFromPropertyData(propertyData)

    // Field mappings: [prefilledKey, formKey]
    const fieldMappings: Array<[keyof typeof prefilledValues, string]> = [
      ['homeSize', 'homeInfo.homeSize'],
      ['numBedrooms', 'homeInfo.numBedrooms'],
      ['numBathrooms', 'homeInfo.numBathrooms'],
      ['numFloors', 'homeInfo.numFloors'],
      ['yearBuilt', 'homeInfo.yearBuilt'],
    ]

    // Apply prefilled values only if form fields are empty; fire FieldSelected per prefilled field
    fieldMappings.forEach(([prefilledKey, formKey]) => {
      const prefilledValue = prefilledValues[prefilledKey]
      if (prefilledValue && !odysseyContext?.getValue(formKey)) {
        odysseyContext?.setValue(formKey, prefilledValue)

        if (tracking) {
          const trackingFieldName = formKeyToTrackingField[formKey]
          if (trackingFieldName) {
            trackFieldSelected({
              formContext: {
                formName: formVersion,
                formType: formType,
                formId,
              },
              stepContext: {
                stepName: tracking.stepName,
                stepId: tracking.stepId || '0',
                stepNumber: currentStepIndex,
              },
              userInputField: {
                fieldName: trackingFieldName,
                fieldType: 'button',
                fieldValue: prefilledValue || '',
                fieldSource: 'rentcast',
                autofilled: true,
              },
            })
          }
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrichCustomerResponse])

  // Expose callback function to parent via ref
  useImperativeHandle(ref, () => ({
    onBeforeNext: async () => {
      const cohesionData: CohesionGlobal = (window as Window)._Cohesion || {}
      const interstitialValue = odysseyContext?.getValue('interstitial')

      try {
        // Complement enrichment data with manual home info from form
        const complementedEnrichment = complementEnrichmentWithFormData(
          enrichCustomerResponse,
          odysseyContext ?? null,
        )

        // Update store with complete enrichment data
        setEnrichCustomerResponse(complementedEnrichment)

        // Fetch consumption prediction if configured to do so
        // (e.g., quick flow where bill upload step is skipped)
        // If prop is undefined, fallback to checking interstitial value (for default questionnaire)
        const shouldFetch =
          shouldFetchConsumptionPrediction ?? interstitialValue === QuestionFlowEnum.QUICK

        if (shouldFetch) {
          // Fetch consumption prediction with complete enrichment data
          await fetchConsumptionPrediction({
            cohesionData,
            enrichedData: complementedEnrichment,
            allEntries: [],
          })
        }

        return true
      } catch (error) {
        incense(error)
          .captureDetails({
            name: 'Questionnaire.HomeInfo.Error',
            message: 'Find My Plan questionnaire | Consumption API Fetch Error',
          })
          .sendError()

        // Allow navigation even if consumption prediction fails
        return true
      }
    },
  }))

  const handleYearBuiltChange = (value: string) => {
    handleFieldSelected('home_details_year_built', value)
    odysseyContext?.setValue('homeInfo.yearBuilt', value)
  }

  const handleFieldSelected = (field: string, value: string) => {
    // Only track if tracking configuration is provided
    if (!tracking) {
      return
    }

    trackFieldSelected({
      formContext: {
        formName: formVersion,
        formType: formType,
        formId,
      },
      stepContext: {
        stepName: tracking.stepName,
        stepId: tracking.stepId || '0',
        stepNumber: currentStepIndex,
      },
      userInputField: {
        fieldName: field,
        fieldType: 'button',
        fieldValue: value || '',
        fieldSource: 'manual',
        autofilled: false,
      },
    })
  }

  return (
    <div>
      <div className='strata:flex-col strata:flex strata:gap-4 strata:mt-3'>
        <div>
          <Toggle
            variant='outlined'
            label='Home size'
            options={[
              {
                label: 'Apartment',
                subtext: '<1000 Sqft',
                value: 'apartment',
              },
              { label: 'Small', subtext: '1000-2500 Sqft', value: 'small' },
              { label: 'Large', subtext: '>2500 Sqft', value: 'large' },
            ]}
            values={[odysseyContext?.getValue('homeInfo.homeSize') as string]}
            onChange={(values: string[]) => {
              handleFieldSelected('home_details_size', values[0] || '')
              odysseyContext?.setValue('homeInfo.homeSize', values[0])
            }}
          />
          <p className='strata:text-left strata:text-system-negative-foreground strata:text-body-xs strata:pt-0.5 strata:@qm/qc:pt-1'>
            {odysseyContext?.getError('homeInfo.homeSize')}
          </p>
        </div>
        <div>
          <Toggle
            variant='outlined'
            label='Number of bedrooms'
            options={oneThroughFivePlusOptions}
            values={[odysseyContext?.getValue('homeInfo.numBedrooms') as string]}
            onChange={(values: string[]) => {
              handleFieldSelected('home_details_bedrooms', values[0] || '')
              odysseyContext?.setValue('homeInfo.numBedrooms', values[0])
            }}
          />
          <p className='strata:text-left strata:text-system-negative-foreground strata:text-body-xs strata:pt-0.5 strata:@qm/qc:pt-1'>
            {odysseyContext?.getError('homeInfo.numBedrooms')}
          </p>
        </div>
        <div>
          <Toggle
            variant='outlined'
            label='Number of bathrooms'
            options={oneThroughFivePlusOptions}
            values={[odysseyContext?.getValue('homeInfo.numBathrooms') as string]}
            onChange={(values: string[]) => {
              handleFieldSelected('home_details_bathrooms', values[0] || '')
              odysseyContext?.setValue('homeInfo.numBathrooms', values[0])
            }}
          />
          <p className='strata:text-left strata:text-system-negative-foreground strata:text-body-xs strata:pt-0.5 strata:@qm/qc:pt-1'>
            {odysseyContext?.getError('homeInfo.numBathrooms')}
          </p>
        </div>
        <div>
          <Toggle
            variant='outlined'
            label='Number of floors'
            options={oneThroughFourPlusOptions}
            values={[odysseyContext?.getValue('homeInfo.numFloors') as string]}
            onChange={(values: string[]) => {
              handleFieldSelected('home_details_floors', values[0] || '')
              odysseyContext?.setValue('homeInfo.numFloors', values[0])
            }}
          />
          <p className='strata:text-left strata:text-system-negative-foreground strata:text-body-xs strata:pt-0.5 strata:@qm/qc:pt-1'>
            {odysseyContext?.getError('homeInfo.numFloors')}
          </p>
        </div>
        <div>
          <label
            htmlFor='approxYearBuilt'
            className='strata:block strata:text-left strata:text-gray-600 strata:text-xs strata:pb-0.5 strata:@qm/qc:pb-1'
          >
            Approx. year built
          </label>
          <div className='strata:w-full'>
            <CustomSelect
              placeholder='Select year'
              className='strata:w-full strata:md:w-1/2'
              triggerClassName=' strata:h-[46px] strata:bg-white strata:border strata:border-secondary-500 strata:px-4 strata:py-3 strata:text-sm strata:font-semibold strata:text-secondary-500 strata:rounded-sm strata:focus-visible:focus-strata-inner strata:cursor-pointer strata:flex strata:items-center strata:justify-between strata:text-left strata:truncate'
              triggerTextAlignment='left'
              options={yearBuiltOptions}
              value={odysseyContext?.getValue('homeInfo.yearBuilt')?.toString() ?? ''}
              onChange={handleYearBuiltChange}
              dropdownIcon={<ArrowDownExpand classNames='strata:text-secondary-500' />}
              data-testid='homeInfo.yearBuilt'
            />

            <p className='strata:text-left strata:text-system-negative-foreground strata:text-body-xs strata:pt-0.5 strata:@qm/qc:pt-1'>
              {odysseyContext?.getError('homeInfo.yearBuilt')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
})

HomeInfo.displayName = 'HomeInfo'
export default HomeInfo
